import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Drop, CheckCircle, Warning, LinkBreak } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * EcowittSettingsCard — connect an Ecowitt soil-moisture sensor network.
 * User needs: application_key, api_key, device MAC (from ecowitt.net portal).
 */
export const EcowittSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ application_key: '', api_key: '', mac: '' });
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ecowitt/credentials`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      setStatus({ configured: false });
    }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.application_key || !form.api_key || !form.mac) {
      toast.error('All three fields required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/ecowitt/credentials`, form, { withCredentials: true });
      toast.success('Ecowitt connected');
      setForm({ application_key: '', api_key: '', mac: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed — double-check your creds');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Ecowitt?')) return;
    try {
      await axios.delete(`${API_URL}/api/ecowitt/credentials`, { withCredentials: true });
      setStatus({ configured: false });
      setLive(null);
      toast.success('Disconnected');
    } catch {
      toast.error('Disconnect failed');
    }
  };

  const fetchLive = async () => {
    setLiveLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ecowitt/live`, { withCredentials: true });
      setLive(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Live read failed');
    } finally {
      setLiveLoading(false);
    }
  };

  const [snapping, setSnapping] = useState(false);
  const snapshotNow = async () => {
    setSnapping(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ecowitt/history/snapshot-now`,
        {},
        { withCredentials: true }
      );
      const stored = res?.data?.stored ?? res?.data?.ok ?? true;
      if (stored) {
        toast.success('Snapshot stored — your Google Sheet will pick it up on the next refresh.');
      } else {
        toast.info(res?.data?.reason || 'Snapshot attempted — check logs.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Snapshot failed');
    } finally {
      setSnapping(false);
    }
  };

  return (
    <Card data-testid="ecowitt-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
              <Drop weight="fill" size={14} /> Ecowitt soil moisture
            </div>
            <h3 className="text-base font-semibold mt-1">Live moisture from your WH51 probes</h3>
            <p className="text-xs text-muted-foreground mt-1">
              If you own Ecowitt WH51 soil-moisture sensors + a GW1000/GW2000 gateway, plug in your API keys and we'll surface live readings on your dashboard.
            </p>
          </div>
          {status?.configured && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 shrink-0">
              <CheckCircle size={10} weight="fill" className="inline mr-0.5" /> Connected
            </span>
          )}
        </div>

        {!status?.configured ? (
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ecowitt-form">
            <div className="md:col-span-3">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Application Key</Label>
              <Input
                value={form.application_key}
                onChange={(e) => setForm({ ...form, application_key: e.target.value })}
                placeholder="from ecowitt.net → User Center → API"
                className="mt-1 font-mono text-xs"
                data-testid="ecowitt-appkey"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">API Key</Label>
              <Input
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="…"
                className="mt-1 font-mono text-xs"
                data-testid="ecowitt-apikey"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Device MAC</Label>
              <Input
                value={form.mac}
                onChange={(e) => setForm({ ...form, mac: e.target.value.toUpperCase() })}
                placeholder="AA:BB:CC:DD:EE:FF"
                className="mt-1 font-mono text-xs"
                data-testid="ecowitt-mac"
              />
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button type="submit" disabled={saving} data-testid="ecowitt-connect">
                {saving ? 'Validating…' : 'Connect'}
              </Button>
            </div>
            <p className="md:col-span-3 text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Warning size={11} className="shrink-0 mt-0.5" />
              Get your keys at <a className="underline" href="https://www.ecowitt.net" target="_blank" rel="noreferrer">ecowitt.net</a> → User Center → API. The device MAC is on the back of your GW1000/GW2000 gateway.
            </p>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Device: <span className="font-mono text-foreground">{status.mac}</span>
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={fetchLive} disabled={liveLoading} data-testid="ecowitt-refresh">
                  {liveLoading ? 'Reading…' : 'Read live data'}
                </Button>
                <Button size="sm" variant="outline" onClick={snapshotNow} disabled={snapping} data-testid="ecowitt-snapshot-now" title="Force-write a row to your history collection — instantly feeds the Decision Engine + Google Sheets export.">
                  {snapping ? 'Saving…' : 'Snapshot now'}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDisconnect} className="text-muted-foreground hover:text-destructive" data-testid="ecowitt-disconnect">
                  <LinkBreak size={14} className="mr-1" /> Disconnect
                </Button>
              </div>
            </div>

            {live && (
              <div className="space-y-3" data-testid="ecowitt-live">
                {live.summary?.avg_moisture_pct != null && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg soil moisture</div>
                        <div className="text-2xl font-bold font-mono">{live.summary.avg_moisture_pct}%</div>
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {live.summary.band}
                      </span>
                    </div>
                    {live.summary.guidance && (
                      <p className="text-xs text-muted-foreground mt-2">{live.summary.guidance}</p>
                    )}
                  </div>
                )}

                {live.soil_channels?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Per-channel readings</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {live.soil_channels.map((c) => (
                        <div key={c.channel} className="rounded border border-border/60 bg-card px-2 py-1.5 text-center">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ch {c.channel}</div>
                          <div className="text-base font-mono font-semibold">{c.moisture_pct}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {live.weather && Object.values(live.weather).some(v => v != null) && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Weather station</div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {live.weather.temp_f != null && <SmallStat label="Temp" value={`${live.weather.temp_f}°F`} />}
                      {live.weather.rh_pct != null && <SmallStat label="RH" value={`${live.weather.rh_pct}%`} />}
                      {live.weather.dewpoint_f != null && <SmallStat label="Dew pt" value={`${live.weather.dewpoint_f}°F`} />}
                      {live.weather.rain_today_in != null && <SmallStat label="Rain today" value={`${live.weather.rain_today_in}"`} />}
                      {live.weather.wind_mph != null && <SmallStat label="Wind" value={`${live.weather.wind_mph} mph`} />}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SmallStat = ({ label, value }) => (
  <div className="rounded border border-border/60 bg-card px-2 py-1 text-center">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-xs font-mono font-semibold">{value}</div>
  </div>
);

export default EcowittSettingsCard;
